import { useEffect, useMemo, useState } from 'react';
import { UIMatch, useMatches } from 'react-router-dom';
import { Button, Flex, Menu as MenuAntd, MenuProps, Space } from 'antd';
import { isMobile } from 'react-device-detect';
import {
  BellOutlined,
  CheckCircleOutlined,
  ClearOutlined,
  DashboardOutlined,
  FileOutlined,
  HomeOutlined,
  MehOutlined,
  MessageOutlined,
  PieChartOutlined,
  SettingOutlined,
  ShoppingOutlined,
  SlidersOutlined,
  TagOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useStore } from '../../config/store';
import { useQuery, useQueryClient } from 'react-query';
import { API } from '../../config/api';
import { InstallButton } from '../InstallButton';
import { MenuItem } from '../MenuItem';
import { isNotificationPermissionGranted, notificationService } from '../../notification-service';
import { bem } from '../../utils/bem';
import './Menu.scss';

const b = bem('menu');

export const MENU_ITEM_ENUM = {
  objects: 'Объекты',
  templates: 'Шаблоны',
  rents: 'Брони',
  canceled_rents: 'Отмены',
  cleaners: 'Горничные',
  cleans: 'Уборки',
  business: 'Бизнес',
  onboarding: 'Обучение',
  tasks: 'Обслуживание',
  updates: 'Обновления',
  support: 'Помощь',
  instructions: 'Инструкции',
  profile: 'Профиль',
  statistics: 'Статистика',
  dashboard: 'Дашборд',
  clienttasks: 'Задачи',
  papertrails: 'История изменений',
  meters: 'Счётчики',
  'cleans/plan': 'Планирование уборок',
  'cleans/report': 'Отчёт по горничным',
  automessages: 'Автосообщения',
  chats: 'Чаты',
  owners: 'Собственники',
};

type Ctx = {
  role: string;
  isWhatsappActive: boolean;
};

type MenuItemBase = {
  key: string;
  label: string;
  icon?: React.ReactNode;
  roles?: string[];
  visibleIf?: (ctx: Ctx) => boolean;
};

type MenuLinkItem = MenuItemBase & {
  link: string;
};

type MenuGroupItem = MenuItemBase & {
  children: MenuItemConfig[];
};

type MenuItemConfig = MenuLinkItem | MenuGroupItem;

const MENU_ITEMS: MenuItemConfig[] = [
  {
    label: 'Дашборд',
    link: '/dashboard',
    key: 'dashboard',
    icon: <PieChartOutlined />,
    roles: ['admin', 'manager', 'fullmanager'],
  },
  {
    label: 'Задачи',
    link: '/clienttasks',
    key: 'clienttasks',
    icon: <CheckCircleOutlined />,
    roles: ['admin', 'manager', 'fullmanager'],
  },
  {
    label: 'Чаты',
    link: '/chats',
    key: 'chats',
    icon: <MessageOutlined />,
    roles: ['admin', 'manager', 'fullmanager'],
    visibleIf: ({ isWhatsappActive }) => isWhatsappActive,
  },
  {
    label: 'Брони',
    link: '/rents',
    key: 'rents',
    icon: <TagOutlined />,
    roles: ['admin', 'manager', 'fullmanager'],
  },
  {
    label: 'Уборки',
    link: '/cleans',
    key: 'cleans',
    icon: <ClearOutlined />,
    roles: ['admin', 'cleaner', 'fullmanager'],
  },
  {
    label: 'Обслуживание',
    link: '/tasks',
    key: 'tasks',
    icon: <TagOutlined />,
    roles: ['admin', 'cleaner', 'fullmanager'],
  },
  {
    label: 'Счётчики',
    link: '/meters',
    key: 'meters',
    icon: <DashboardOutlined />,
    roles: ['admin', 'manager', 'fullmanager'],
  },
  {
    label: 'Собственники',
    link: '/owners',
    key: 'owners',
    icon: <ShoppingOutlined />,
    roles: ['admin'],
  },
  {
    label: 'Настройки',
    key: 'settings',
    icon: <SettingOutlined />,
    children: [
      {
        label: 'Объекты',
        link: '/objects',
        key: 'objects',
        icon: <HomeOutlined />,
        roles: ['admin', 'manager', 'fullmanager'],
      },
      {
        label: 'Инструкции',
        link: '/instructions',
        key: 'instructions',
        icon: <FileOutlined />,
        roles: ['admin', 'fullmanager'],
      },
      {
        label: 'Горничные',
        link: '/cleaners',
        key: 'cleaners',
        icon: <UserOutlined />,
        roles: ['admin', 'cleaner', 'fullmanager'],
      },
      {
        label: 'Бизнес',
        link: '/business',
        key: 'business',
        icon: <SlidersOutlined />,
        roles: ['admin', 'cleaner', 'fullmanager'],
      },
      {
        label: 'Профиль',
        link: '/profile',
        key: 'profile',
        icon: <MehOutlined />,
        roles: ['admin', 'manager', 'cleaner', 'fullmanager'],
      },
    ],
  },
];

const filterMenu = (items: MenuItemConfig[], ctx: Ctx): MenuItemConfig[] => {
  return items
    .filter((item) => {
      if (item.roles && !item.roles.includes(ctx.role)) return false;
      if (item.visibleIf && !item.visibleIf(ctx)) return false;
      return true;
    })
    .map((item) =>
      'children' in item
        ? {
            ...item,
            children: filterMenu(item.children, ctx),
          }
        : item,
    )
    .filter((item) => !('children' in item) || item.children.length > 0);
};

const findOpenKeys = (items: MenuItemConfig[], targetKey: string, parents: string[] = []): string[] => {
  for (const item of items) {
    if (item.key === targetKey) {
      return parents;
    }

    if ('children' in item) {
      const result = findOpenKeys(item.children, targetKey, [...parents, item.key]);

      if (result.length) {
        return result;
      }
    }
  }

  return [];
};

export const Menu = ({ set }: { set: () => void }) => {
  const { data: count } = useQuery('clientTasksCount', API.clienttasks.getClientTasksCount);
  const { data: objectsCount } = useQuery('objectsCount', API.objects.getUnloadedObjectsCount);
  const { data: automessagesData } = useQuery('automessages', API.settings.getAutomessages);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const matches = useMatches() as UIMatch<undefined, { menuKey?: string } | undefined>[];
  const queryClient = useQueryClient();
  const { role } = useStore();

  const onClick: MenuProps['onClick'] = () => {
    queryClient.invalidateQueries('clientTasksCount');
    queryClient.invalidateQueries('objectsCount');

    if (isMobile) {
      set();
    }
  };

  const badges = useMemo(() => {
    return {
      clienttasks: count,
      objects: objectsCount,
    };
  }, [count, objectsCount]);

  const filteredMenuItems = useMemo(() => {
    const whatsappProfile = automessagesData?.wappi_profiles?.find((profile) => profile.type === 'whatsapp');
    const isWhatsappActive =
      (automessagesData?.active && whatsappProfile?.active && whatsappProfile?.authorized) || false;

    return filterMenu(MENU_ITEMS, {
      role,
      isWhatsappActive,
    });
  }, [automessagesData, role]);

  const selectedKey = useMemo(() => {
    return [...matches].reverse().find((m) => m?.handle?.menuKey)?.handle?.menuKey;
  }, [matches]);

  useEffect(() => {
    setOpenKeys(selectedKey ? findOpenKeys(filteredMenuItems, selectedKey) : []);
  }, [filteredMenuItems, selectedKey]);

  const menuItems = useMemo((): NonNullable<MenuProps['items']> => {
    const toItems = (items: MenuItemConfig[]): NonNullable<MenuProps['items']> =>
      items.map((item) => {
        if ('children' in item) {
          return {
            key: item.key,
            icon: item.icon,
            label: item.label,
            children: toItems(item.children),
          };
        }
        return {
          key: item.key,
          icon: item.icon,
          label: <MenuItem menuKey={item.key} to={item.link} label={item.label} badges={badges} />,
        };
      });
    return toItems(filteredMenuItems);
  }, [filteredMenuItems, badges]);

  return (
    <>
      <Flex className={b()} align="center" vertical gap="10px">
        <div className="logo_block">
          <Space direction="vertical">
            <img src="/autopilot.svg" className="logo" alt="Autopilot logo" />
            {localStorage.getItem('agency_id')}
          </Space>
        </div>
        <InstallButton />
        {!isNotificationPermissionGranted() && (
          <Button
            style={{ alignSelf: 'center' }}
            type="primary"
            icon={<BellOutlined />}
            onClick={() => notificationService.requestNotificationPermission()}
          >
            Включить уведомления
          </Button>
        )}
        <MenuAntd
          onClick={onClick}
          theme="dark"
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : undefined}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          items={menuItems}
        />
      </Flex>
    </>
  );
};
